"use client";

import type { ReactNode } from "react";
import { TrackingBoundary } from "../../../lib/sentry/index";
import { ShellTrigger } from "./shell/ResponsiveShell";

export interface RelayDepositTriggerProps {
  /** Render the child as the trigger instead of wrapping it. Defaults to
   * `true` whenever a child is supplied, matching
   * `WalletConnectModalTrigger`'s `asChild ?? children != null`. */
  asChild?: boolean;
  children?: ReactNode;
}

/**
 * Thin pass-through to `ShellTrigger` (radix `Dialog.Trigger` on desktop /
 * vaul `Drawer.Trigger` on mobile) so hosts write
 * `<RelayDeposit.Trigger><button>…</button></RelayDeposit.Trigger>` without
 * knowing which primitive is active. Falls back to a plain default button when
 * no child is given, same as `WalletConnectModalTrigger`.
 *
 * Wrapped in `<TrackingBoundary feature="relay">` for the same reason
 * `WalletConnectModalTrigger` is: the `data-track="open"` attribute below (and
 * on any host-supplied child that carries one) is only picked up by the
 * analytics delegate a boundary installs. Without it the open click is silently
 * untracked, since the trigger lives OUTSIDE the portaled `<Content>` boundary.
 */
export function RelayDepositTrigger({ asChild, children }: RelayDepositTriggerProps) {
  return (
    <TrackingBoundary feature="relay">
      <ShellTrigger asChild={asChild ?? children != null}>
        {children ?? (
          <button type="button" data-track="open">
            Deposit
          </button>
        )}
      </ShellTrigger>
    </TrackingBoundary>
  );
}
